import moment from 'moment'
import React, { useMemo, useEffect } from 'react'
import FormatCurrency from 'components/Common/FormatCurrency'
import { pluralizeToString } from 'lib/string/pluralize'
import Pill from 'components/Luxkit/Pill'
import styled from 'styled-components'
import rem from 'polished/lib/helpers/rem'
import Caption from 'components/Luxkit/Typography/Caption'
import BodyText from 'components/Luxkit/Typography/BodyText'
import { getHotelOfferDisplayedValue } from 'lib/offer/getOfferDisplayPricing'
import * as Analytics from 'analytics/analytics'
import { logOperational } from 'analytics/snowplow/events'
import { getCheapestPackageForDuration } from '../Common/getCheapestPackageForDuration'
import config from 'constants/config'

const DiscountPill = styled(Pill)`
  margin-top: ${rem(4)};
`

const PriceTextGroup = styled.div`
  display: flex;
  align-items: center;
  padding-right: ${rem(8)};

  ${DiscountPill} {
    margin-left: ${rem(8)};
  }
`

interface Props {
  duration?: number;
  offer: App.Offer | App.OfferSummary;
  flightOrigin: string;
  alternativeCheckIn?: string;
  alternativeCheckOut?: string;
  alternativePrice?: number;
}

const ALTERNATIVE_DATE_FORMAT = 'DD MMM'

function HotelPriceContent({
  duration,
  offer,
  flightOrigin,
  alternativeCheckIn,
  alternativeCheckOut,
  alternativePrice,
}: Props) {
  const enableDurationSearch = !!config.LERE_ENABLE_CALENDAR_DURATION_FILTER
  const showAlternativeDates = alternativeCheckIn && alternativeCheckOut && alternativePrice

  const { flightPrices, shouldDisplayValue } = offer
  const lowestPricePackage = useMemo<App.Package | null>(() => getCheapestPackageForDuration({
    enableDurationSearch,
    offer,
    duration,
  }), [offer, duration, enableDurationSearch])

  useEffect(() => {
    if (!Number.isFinite(duration)) {
      Analytics.track('log-operational', logOperational('HotelPriceContent', `duration_not_finite::${duration}`))
    }
  }, [duration])

  const flightPrice = flightPrices[flightOrigin]

  const packageCalculatedValues = useMemo(() => {
    const packageToDisplay = showAlternativeDates ? (offer.packages.find(p => p.price === alternativePrice) || lowestPricePackage) : lowestPricePackage
    if (!packageToDisplay) {
      return null
    }
    const { price, value } = packageToDisplay

    return {
      hotelOfferDisplayedValue: getHotelOfferDisplayedValue(offer.bundledWithFlightsOnly, price, value, shouldDisplayValue, flightPrice),
      packageToDisplay,
    }
  }, [
    alternativePrice,
    flightPrice,
    lowestPricePackage,
    offer.bundledWithFlightsOnly,
    offer.packages,
    shouldDisplayValue,
    showAlternativeDates,
  ])

  if (!packageCalculatedValues) {
    return null
  }

  const {
    hotelOfferDisplayedValue: {
      highestDiscountPercent,
      shouldShowValue,
      shouldShowDiscountPercent,
    },
    packageToDisplay: {
      price,
      duration: durationToDisplay,
      value,
    },
  } = packageCalculatedValues

  const isDiscountPillHidden = offer.isDiscountPillHidden

  const captionText = showAlternativeDates ?
    `${moment(alternativeCheckIn).format(ALTERNATIVE_DATE_FORMAT)} - ${moment(alternativeCheckOut).format(ALTERNATIVE_DATE_FORMAT)} from` :
    `${pluralizeToString('night', durationToDisplay)} from`
  const priceText = showAlternativeDates ? Math.ceil(alternativePrice) : Math.ceil(price)

  return (
    <>
      <Caption variant="medium">{captionText}</Caption>
      <BodyText variant="small"><BodyText variant="large" weight="bold" as="span"><FormatCurrency value={priceText} /></BodyText> /{offer.saleUnit}
      </BodyText>
      {!showAlternativeDates && <PriceTextGroup>
        {shouldShowValue && <Caption variant="medium" weight="bold">Valued up to <FormatCurrency value={Math.ceil(value)} /></Caption>}
        {shouldShowDiscountPercent && !isDiscountPillHidden && (
          <DiscountPill kind="primary" variant="success">
            -{highestDiscountPercent}%
          </DiscountPill>
        )}
      </PriceTextGroup>}
    </>
  )
}

export default HotelPriceContent
